import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-63dc40c1"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "person-highlights" }
const _hoisted_2 = { class: "row highlight-row" }
const _hoisted_3 = {
  key: 0,
  class: "empty-list col"
}
const _hoisted_4 = {
  key: 2,
  class: "other-highlight"
}
const _hoisted_5 = { class: "highlight-visual" }
const _hoisted_6 = {
  key: 0,
  class: "highlight-icon"
}
const _hoisted_7 = ["src"]
const _hoisted_8 = { class: "highlight-text" }

import { Ref, computed, ref, watch } from 'vue'
import { usePlaceStore } from '@/rd/PlaceStore'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { getPlacePopulation } from './PlacePopulation'
import { usePersonNameHighlights } from './PersonNameHighlights'
import { HighlightItem } from './HighlightItem'
import { getPossessivePhrase } from '@/util/LanguageUtil'
import PopulationHighlight from './PopulationHighlight.vue'
import PersonNameHighlight from './PersonNameHighlight.vue'
import { HistoricalDateType } from '@/rd/HistoricalDateEnums'
import { DurationUtil } from '@/util/LuxonUtil'
import { FamilyAndChildren, useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { getFamilyHighlights, getParentHighlights } from './FamilyHighlights'


export default /*#__PURE__*/_defineComponent({
  __name: 'PersonHighlights',
  props: {
  personId: String
},
  setup(__props, { expose: __expose }) {

const props = __props;



__expose({
  getCount: () => highlightsRef.value.length,
})

const highlightsRef = ref([]) as Ref<HighlightItem[]>

const viewPersonStore = useViewPersonStore()
const viewFamilyStore = useViewFamilyStore()
const placeStore = usePlaceStore()
const { getNameHighlights } = usePersonNameHighlights()

const viewPersonRef = computed(() => viewPersonStore.getAsyncPerson(props.personId)?.data)
const siblingsRef = computed(() => viewPersonStore.getLoadedParentFamilies(props.personId ?? '').parentFamilies
  .flatMap(vf => viewFamilyStore.getLoadedChildren(vf.id!)))

watch([viewPersonRef, siblingsRef], () => {
  highlightsRef.value = []

  if (!viewPersonRef.value)
    return

  // lifespan
  const birthDate = viewPersonRef.value.displayProperties.birthDate
  const deathDate = viewPersonRef.value.displayProperties.deathDate
  if (!birthDate.isEmpty && 
    !deathDate.isEmpty && 
    birthDate.type == HistoricalDateType.Date && 
    deathDate.type == HistoricalDateType.Date) {
    
    const lifespan = deathDate.date1!.diff(birthDate.date1!).shiftTo("years", "months", "days")
    if (lifespan.years < 30) {
      const text = (lifespan.as('days') == 0) ? 'died at birth' : `lived only ${DurationUtil.toAge(lifespan, true)}`
      highlightsRef.value.push({
        id: `lifespan:short:${lifespan.toISO()}`,
        text,
        value: DurationUtil.toAge(lifespan),
      })
    }
  }
    
  const birthPlaceId = viewPersonRef.value.displayProperties.birthPlaceId
  if (birthPlaceId) {
    const birthPlace = placeStore.asyncPlaces.get(birthPlaceId)?.data
    if (birthPlace?.name) {

      // birth place population
      const pop = getPlacePopulation(birthPlace.name, viewPersonRef.value.birthYear)
      if (pop) {
        highlightsRef.value.push({
          id: `population:${viewPersonRef.value.displayProperties.birthPlaceId}:birth:${viewPersonRef.value.birthYear}`,
          text: `hometown population at ${getPossessivePhrase(viewPersonRef.value.displayProperties.gender, 'birth')}`,
          value: pop,
        })
      }

      // birth place country
      if (birthPlace.countryCode) {
        highlightsRef.value.push({
          id: `birthPlace:${birthPlace.countryCode}`,
          text: `born in ${birthPlace.countryName}`,
          flag: birthPlace.countryCode.toLowerCase(),
        })
      }

      // death place country (if different)
      const deathPlaceId = viewPersonRef.value.displayProperties.deathPlaceId
      if (deathPlaceId) {
        const deathPlace = placeStore.asyncPlaces.get(deathPlaceId)?.data
        if (deathPlace?.countryCode && deathPlace.countryCode != birthPlace.countryCode) {
          highlightsRef.value.push({
            id: `deathPlace:${deathPlace.countryCode}`,
            text: `died in ${deathPlace.countryName}`,
            flag: deathPlace.countryCode.toLowerCase(),
          })
        }
      }
    }
  }

  // name highlights
  highlightsRef.value.push(...getNameHighlights(props.personId!))

  // family highlights
  for (const pf of viewPersonStore.getLoadedParentFamilies(props.personId!).parentFamilies) {
    const children = viewFamilyStore.getLoadedChildren(pf.id!).children
    highlightsRef.value.push(...getFamilyHighlights(pf, children, props.personId!))
  }

  // parent highlights
  const spouseFamilies = viewPersonStore.getLoadedSpouseFamilies(props.personId!).spouseFamilies
  const familiesAndChildren = spouseFamilies.map(sf => {
    const children = viewFamilyStore.getLoadedChildren(sf.id!).children
    return { family: sf, childIds: children.map(ch => ch.id!) } as FamilyAndChildren
  })
  highlightsRef.value.push(...getParentHighlights(viewPersonRef.value, familiesAndChildren))
})

function getFittedFontSize(value: string | number) {
  const valueStr = value.toString()

  if (valueStr.length < 3) {
    return '2rem'
  } else if (valueStr.length < 4) {
    return '1.3rem'
  } else {
    return '1.1rem'
  }
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (highlightsRef.value.length == 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, " No fun facts found (yet) "))
        : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(highlightsRef.value, (h) => {
        return (_openBlock(), _createElementBlock("div", {
          key: h.id,
          class: "col-3"
        }, [
          (h.id.startsWith('population:'))
            ? (_openBlock(), _createBlock(PopulationHighlight, {
                key: 0,
                highlight: h
              }, null, 8, ["highlight"]))
            : (h.id.startsWith('sameName:'))
              ? (_openBlock(), _createBlock(PersonNameHighlight, {
                  key: 1,
                  highlight: h
                }, null, 8, ["highlight"]))
              : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    (h.image)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                          _createElementVNode("img", {
                            src: h.image
                          }, null, 8, _hoisted_7)
                        ]))
                      : _createCommentVNode("", true),
                    (h.value)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 1,
                          class: "highlight-value",
                          style: _normalizeStyle({ fontSize: getFittedFontSize(h.value) })
                        }, _toDisplayString(h.value), 5))
                      : _createCommentVNode("", true),
                    (h.flag)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 2,
                          class: _normalizeClass(["highlight-flag fi", [`fi-${h.flag}`]])
                        }, null, 2))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("div", _hoisted_8, _toDisplayString(h.text), 1)
                ]))
        ]))
      }), 128))
    ])
  ]))
}
}

})